import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Loader2 } from 'lucide-react';
import { normalizeUrl, SITE_URL, getSEOConfig } from '../config/seoConfig';

// Use environment variable for API base URL to avoid hydration mismatch
const API_BASE = window.location.origin || '';

// Static SEO content for pre-rendering (used when API is unavailable)
const staticSeoContent = {
  '/istanbul-airport-transfer/': {
    title: 'Istanbul Airport Transfer (IST & SAW) | Fixed Price',
    description: 'Private Istanbul Airport transfers from IST & SAW to Taksim, Sultanahmet & all hotels. Fixed price, meet & greet, flight tracking. Book online in minutes.',
    h1: 'Istanbul Airport Transfer Service',
    content: `
      <h1>Istanbul Airport Transfer Service</h1>
      <p>Book your private VIP transfer from Istanbul Airport (IST) or Sabiha Gokcen Airport (SAW) to any destination in Istanbul and beyond.</p>
      <h2>Why Choose Our Istanbul Airport Transfer?</h2>
      <ul>
        <li>Fixed prices with no hidden fees</li>
        <li><strong>Free Meet & Greet</strong> service at arrivals</li>
        <li><strong>Free Cancellation</strong> up to 24 hours before pickup</li>
        <li>Free flight monitoring</li>
        <li>Professional English-speaking drivers</li>
        <li>Luxury Mercedes vehicles</li>
        <li>24/7 customer support</li>
      </ul>
      <h2>Istanbul Airport (IST) Transfer Prices</h2>
      <ul>
        <li>Istanbul Airport to Taksim - from €45</li>
        <li>Istanbul Airport to Sultanahmet - from €45</li>
        <li>Istanbul Airport to Kadikoy - from €50</li>
      </ul>
    `
  },
  '/antalya-airport-transfer/': {
    title: 'Antalya Airport Transfer (AYT) | All Resorts',
    description: 'Book your Antalya Airport (AYT) transfer to Belek, Side, Kemer, Lara & Alanya. Fixed-price private cars, English-speaking drivers, 24/7 meet & greet.',
    h1: 'Antalya Airport Transfer Service',
    content: `
      <h1>Antalya Airport Transfer Service</h1>
      <p>Book your private VIP transfer from Antalya Airport (AYT) to your resort destination along the Turkish Riviera.</p>
      <div style="background-color:#dcfce7;border:1px solid #86efac;border-radius:8px;padding:15px;margin:20px 0;">
        <p style="margin:0;color:#166534;"><strong>Free Meet & Greet</strong> + <strong>Free Cancellation</strong> up to 24h</p>
      </div>
      <h2>Popular Destinations from Antalya Airport</h2>
      <ul>
        <li>Antalya Airport to Kemer - 45 min</li>
        <li>Antalya Airport to Belek - 35 min</li>
        <li>Antalya Airport to Side - 1 hour</li>
        <li>Antalya Airport to Alanya - 2 hours</li>
      </ul>
    `
  },
  '/dalaman-airport-transfer/': {
    title: 'Dalaman Airport Transfer (DLM) | Fethiye, Marmaris, Ölüdeniz',
    description: 'Private Dalaman Airport transfers to Fethiye, Ölüdeniz, Marmaris, Kalkan & Kaş. Fixed prices, child seats, free flight monitoring. Reserve online now.',
    h1: 'Dalaman Airport Transfer Service',
    content: `
      <h1>Dalaman Airport Transfer Service</h1>
      <p>Private VIP transfers from Dalaman Airport (DLM) to Fethiye, Marmaris, Oludeniz and surrounding areas.</p>
      <div style="background-color:#dcfce7;border:1px solid #86efac;border-radius:8px;padding:15px;margin:20px 0;">
        <p style="margin:0;color:#166534;"><strong>Free Meet & Greet</strong> + <strong>Free Cancellation</strong> up to 24h</p>
      </div>
      <h2>Destinations from Dalaman Airport</h2>
      <ul>
        <li>Dalaman Airport to Fethiye - 45 min</li>
        <li>Dalaman Airport to Oludeniz - 1 hour</li>
        <li>Dalaman Airport to Marmaris - 1.5 hours</li>
      </ul>
    `
  },
  '/bodrum-airport-transfer/': {
    title: 'Bodrum Airport Transfer (BJV) | Private Peninsula',
    description: 'Bodrum Airport (BJV) transfers to Bodrum, Turgutreis, Yalıkavak & Gümbet. Fixed-price private vehicles, English-speaking drivers, 24/7 support.',
    h1: 'Bodrum Airport Transfer Service',
    content: `
      <h1>Bodrum Airport Transfer Service</h1>
      <p>Private transfers from Bodrum Airport (BJV) to Bodrum town and all peninsula destinations.</p>
      <div style="background-color:#dcfce7;border:1px solid #86efac;border-radius:8px;padding:15px;margin:20px 0;">
        <p style="margin:0;color:#166534;"><strong>Free Meet & Greet</strong> + <strong>Free Cancellation</strong> up to 24h</p>
      </div>
      <h2>Bodrum Peninsula Destinations</h2>
      <ul>
        <li>Bodrum Airport to Bodrum Center - 35 min</li>
        <li>Bodrum Airport to Yalikavak - 45 min</li>
        <li>Bodrum Airport to Turgutreis - 40 min</li>
      </ul>
    `
  },
  '/turkey-vip-transfer-services/': {
    title: 'VIP Transfer Services Turkey | Premium Airport Transfers',
    description: 'Premium VIP transfer services across Turkey. Luxury vehicles, professional chauffeurs, free meet & greet, free cancellation.',
    h1: 'VIP Transfer Services in Turkey',
    content: `
      <h1>VIP Transfer Services in Turkey</h1>
      <p>GoTurkeyTransfer offers premium VIP airport transfer services across all major Turkish airports.</p>
      <div style="background-color:#dcfce7;border:1px solid #86efac;border-radius:8px;padding:15px;margin:20px 0;">
        <p style="margin:0;color:#166534;"><strong>Free Meet & Greet</strong> + <strong>Free Cancellation</strong> up to 24h</p>
      </div>
      <h2>Our VIP Services Include</h2>
      <ul>
        <li><strong>Free Meet & Greet</strong> at arrivals</li>
        <li><strong>Free Cancellation</strong> up to 24 hours before pickup</li>
        <li>Luxury Mercedes vehicles</li>
        <li>Professional chauffeurs</li>
        <li>Free Wi-Fi in vehicles</li>
      </ul>
    `
  }
};

const CmsPage = () => {
  const { '*': pathParam } = useParams();
  const location = useLocation();

  // Get normalized path
  const getUrlPath = () => {
    let path = pathParam || location.pathname;
    // Normalize: remove trailing slash (except for root)
    path = normalizeUrl(path);
    return path;
  };

  const urlPath = getUrlPath();

  // Prerendered CMS page bootstrap (injected by scripts/prerender-cms.mjs)
  const prerendered = typeof window !== 'undefined' ? window.__PRERENDERED_CMS_PAGE__ : null;
  const hasPrerenderedPage = Boolean(prerendered?.urlPath === urlPath && prerendered?.page);

  const [page, setPage] = useState(hasPrerenderedPage ? prerendered.page : null);
  const [isLoading, setIsLoading] = useState(!hasPrerenderedPage);
  const [error, setError] = useState(null);
  const contentRef = useRef(null);

  // Get pre-rendered SSR content for this specific page (only on initial page load).
  // This prevents blank screens / cloaking: users see static content while API loads.
  const ssrHtml = useMemo(() => {
    const ssr = window.__SSR_CONTENT__;
    if (ssr && ssr.html) {
      const ssrPath = normalizeUrl(ssr.path);
      const currentPath = normalizeUrl(urlPath);
      if (ssrPath === currentPath) {
        return ssr.html;
      }
    }
    return null;
  }, [urlPath]);

  useEffect(() => {
    if (hasPrerenderedPage) {
      return;
    }

    const fetchPage = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Remove leading slash for API call
        const apiPath = urlPath.replace(/^\//, '').replace(/\/$/, '');
        const response = await fetch(`${API_BASE}/api/cms/page/${apiPath}`);
        if (response.status === 404) {
          setError('not-found');
          return;
        }
        if (!response.ok) {
          throw new Error(`CMS request failed with status ${response.status}`);
        }
        const data = await response.json();
        
        if (data.success && data.page) {
          setPage(data.page);
        } else {
          setError('not-found');
        }
      } catch (err) {
        console.error('Failed to fetch CMS page:', err);
        setError('load-failed');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPage();
  }, [hasPrerenderedPage, urlPath]);

  // Set SEO meta tags from CMS data ONLY when seoConfig.js doesn't cover this page
  useEffect(() => {
    if (!page && !error) return; // Skip during loading

    // Check if seoConfig.js already handles this page (pre-rendered at build time)
    const seoConfig = getSEOConfig(urlPath);
    const hasCentralConfig = seoConfig && seoConfig.title && !seoConfig.title.startsWith('GoTurkeyTransfer |');

    // If static pre-rendered config exists, skip client-side SEO override
    if (hasCentralConfig) return;

    // Helper to create/update meta tag
    const setMeta = (selector, content) => {
      if (!content) return;
      let meta = document.querySelector(selector);
      if (!meta) {
        meta = document.createElement('meta');
        if (selector.includes('name="')) {
          const name = selector.match(/name="([^"]+)"/)?.[1];
          if (name) meta.setAttribute('name', name);
        }
        if (selector.includes('property="')) {
          const property = selector.match(/property="([^"]+)"/)?.[1];
          if (property) meta.setAttribute('property', property);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Helper to set canonical
    const setCanonical = (href) => {
      let canonicalLink = document.querySelector('link[rel="canonical"]');
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute('href', href);
    };

    if (page) {
      const title = page.seoTitle || page.title || 'GoTurkeyTransfer';
      const description = page.metaDescription || '';
      const canonical = `${SITE_URL}${urlPath.endsWith('/') ? urlPath : urlPath + '/'}`;
      const robots = page.robotsMeta || 'index, follow';
      const ogImage = page.ogImage || `${SITE_URL}/og-image.png`;

      document.title = title;
      setMeta('meta[name="description"]', description);
      setMeta('meta[name="robots"]', robots);
      setMeta('meta[name="googlebot"]', robots);
      setCanonical(canonical);

      setMeta('meta[property="og:type"]', 'article');
      setMeta('meta[property="og:url"]', canonical);
      setMeta('meta[property="og:title"]', page.ogTitle || title);
      setMeta('meta[property="og:description"]', page.ogDescription || description);
      setMeta('meta[property="og:image"]', ogImage);

      setMeta('meta[name="twitter:card"]', 'summary_large_image');
      setMeta('meta[name="twitter:title"]', page.ogTitle || title);
      setMeta('meta[name="twitter:description"]', page.ogDescription || description);
      setMeta('meta[name="twitter:image"]', page.twitterImage || ogImage);

      // Add Service schema for transfer pages
      const existingSchema = document.querySelector('script[data-cms-schema]');
      if (existingSchema) existingSchema.remove();

      const serviceSchema = {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": title,
        "description": description,
        "provider": {
          "@type": "Organization",
          "@id": `${SITE_URL}/#organization`,
          "name": "GoTurkeyTransfer"
        },
        "serviceType": "Airport Transfer",
        "areaServed": { "@type": "Country", "name": "Turkey" },
        "url": canonical
      };

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-cms-schema', 'true');
      script.textContent = JSON.stringify(serviceSchema);
      document.head.appendChild(script);

    } else if (error) {
      // No central config and CMS error — use static fallback if available
      const staticContent = staticSeoContent[urlPath] || staticSeoContent[urlPath + '/'];
      if (staticContent) {
        document.title = staticContent.title;
        setMeta('meta[name="description"]', staticContent.description);
        setMeta('meta[name="robots"]', 'index, follow');
        setCanonical(`${SITE_URL}${urlPath.endsWith('/') ? urlPath : urlPath + '/'}`);
      } else {
        document.title = 'Page Not Found | GoTurkeyTransfer';
        setMeta('meta[name="robots"]', 'noindex, nofollow');
      }
    }
  }, [page, error, urlPath]);

  // Initialize FAQ accordion functionality after page loads
  useEffect(() => {
    if (page && contentRef.current) {
      const faqItems = contentRef.current.querySelectorAll('.faq-item');
      
      faqItems.forEach(item => {
        const trigger = item.querySelector('.faq-trigger');
        const content = item.querySelector('.faq-content');
        const icon = item.querySelector('.faq-icon');
        
        if (trigger && content && icon) {
          // Remove existing listeners
          const newTrigger = trigger.cloneNode(true);
          trigger.parentNode.replaceChild(newTrigger, trigger);
          
          newTrigger.addEventListener('click', function(e) {
            e.preventDefault();
            const isOpen = !content.classList.contains('hidden');
            
            // Close all other items
            faqItems.forEach(otherItem => {
              const otherContent = otherItem.querySelector('.faq-content');
              const otherIcon = otherItem.querySelector('.faq-icon');
              const otherTrigger = otherItem.querySelector('.faq-trigger');
              if (otherContent && otherIcon && otherTrigger) {
                otherContent.classList.add('hidden');
                otherIcon.style.transform = 'rotate(0deg)';
                otherTrigger.setAttribute('aria-expanded', 'false');
              }
            });
            
            // Toggle current item
            if (!isOpen) {
              content.classList.remove('hidden');
              icon.style.transform = 'rotate(180deg)';
              newTrigger.setAttribute('aria-expanded', 'true');
            }
          });
        }
      });
    }
  }, [page]);

  // Fix relative image URLs in content and replace old domains
  useEffect(() => {
    if (page && contentRef.current) {
      const images = contentRef.current.querySelectorAll('img');
      images.forEach(img => {
        let src = img.getAttribute('src');
        if (src) {
          // Fix old domain references
          if (src.includes('vipgez-premium.emergent.host')) {
            src = src.replace('https://vipgez-premium.emergent.host', 'https://goturkeytransfer.com');
            img.setAttribute('src', src);
          }
          // Handle relative URLs
          else if (!src.startsWith('http://') && !src.startsWith('https://') && !src.startsWith('data:')) {
            // Relative URL - prepend API_BASE
            const newSrc = src.startsWith('/') ? `${API_BASE}${src}` : `${API_BASE}/${src}`;
            img.setAttribute('src', newSrc);
          }
        }
      });
    }
  }, [page]);

  if (isLoading) {
    // If SSR pre-rendered content exists, show it instead of a spinner.
    // This eliminates blank pages and cloaking: users see real content while API loads.
    if (ssrHtml) {
      return (
        <div className="min-h-screen bg-background">
          <Header />
          <main className="pt-32 md:pt-36 pb-16">
            <div dangerouslySetInnerHTML={{ __html: ssrHtml }} />
          </main>
          <Footer />
        </div>
      );
    }
    // No SSR fallback available — show minimal loading spinner
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-32 md:pt-36 pb-16">
          <div className="max-w-4xl mx-auto px-4">
            <div className="flex items-center justify-center min-h-[400px]">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error === 'not-found' || (!page && !error && !isLoading)) {
    // If SSR pre-rendered content exists, show it permanently.
    // API failed but user still sees meaningful content — no blank pages.
    if (ssrHtml) {
      return (
        <div className="min-h-screen bg-background">
          <Header />
          <main className="pt-32 md:pt-36 pb-16">
            <div dangerouslySetInnerHTML={{ __html: ssrHtml }} />
          </main>
          <Footer />
        </div>
      );
    }

    // Check if we have static content for this path (hardcoded fallback)
    const staticContent = staticSeoContent[urlPath] || staticSeoContent[urlPath + '/'];
    
    if (staticContent) {
      // Render static SEO content when API fails
      return (
        <div className="min-h-screen bg-background">
          <Header />
          <main className="pt-32 md:pt-36 pb-16">
            <div className="max-w-4xl mx-auto px-4">
              <article 
                className="prose prose-invert prose-lg max-w-none
                  prose-headings:text-foreground prose-headings:font-display
                  prose-h1:text-3xl prose-h1:md:text-4xl prose-h1:mb-6
                  prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
                  prose-p:text-muted-foreground prose-p:leading-relaxed
                  prose-li:text-muted-foreground
                  prose-ul:text-muted-foreground"
                dangerouslySetInnerHTML={{ __html: staticContent.content }}
              />
            </div>
          </main>
          <Footer />
        </div>
      );
    }
    
    // No static content available - show 404 with noindex
    return (
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>Page Not Found | GoTurkeyTransfer</title>
          <meta name="robots" content="noindex, nofollow" />
          <link rel="canonical" href="https://goturkeytransfer.com/404" />
        </Helmet>
        <Header />
        <main className="pt-24 pb-16">
          <div className="max-w-4xl mx-auto px-4 text-center min-h-[400px] flex flex-col items-center justify-center">
            <h1 className="text-2xl font-bold text-foreground mb-4">Page Not Found</h1>
            <p className="text-muted-foreground">The page you&apos;re looking for doesn&apos;t exist.</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error === 'load-failed') {
    // If SSR pre-rendered content exists, show it permanently.
    if (ssrHtml) {
      return (
        <div className="min-h-screen bg-background">
          <Header />
          <main className="pt-32 md:pt-36 pb-16">
            <div dangerouslySetInnerHTML={{ __html: ssrHtml }} />
          </main>
          <Footer />
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-24 pb-16">
          <div className="max-w-4xl mx-auto px-4 text-center min-h-[400px] flex flex-col items-center justify-center">
            <h1 className="text-2xl font-bold text-foreground mb-4">Page temporarily unavailable</h1>
            <p className="text-muted-foreground">Please try again in a few moments.</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-32 md:pt-36 pb-16">
        <div className="max-w-4xl mx-auto px-4">
          {/* Page Content */}
          <article 
            ref={contentRef}
            className="prose prose-invert prose-lg max-w-none
              prose-headings:text-foreground prose-headings:font-display
              prose-h1:text-3xl prose-h1:md:text-4xl prose-h1:mb-6 prose-h1:mt-0
              prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
              prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
              prose-p:text-muted-foreground prose-p:leading-relaxed prose-p:mb-4
              prose-a:text-primary prose-a:no-underline hover:prose-a:underline
              prose-strong:text-foreground
              prose-img:rounded-lg prose-img:mx-auto
              prose-li:text-muted-foreground
              prose-ul:text-muted-foreground
              prose-ol:text-muted-foreground"
            dangerouslySetInnerHTML={{ __html: page.htmlContent || '' }}
            data-testid="cms-content"
          />
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default CmsPage;
